import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  signal,
} from "@angular/core";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { catchError, debounceTime, distinctUntilChanged, of, Subject, switchMap } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ApiService } from "../core/api.service";
import { ConfirmDialogService } from "../core/confirm-dialog.service";

interface EmailTemplateMeta {
  name: string;
  overridden: boolean;
  updatedAt?: string;
}

@Component({
  selector: "app-email-templates-page",
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: "./email-templates-page.component.html",
  styleUrls: ["./email-templates-page.component.scss"],
})
export class EmailTemplatesPageComponent {
  private readonly api = inject(ApiService);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly destroyRef = inject(DestroyRef);
  private readonly confirmDialog = inject(ConfirmDialogService);

  readonly templates = signal<EmailTemplateMeta[]>([]);
  readonly loading = signal(true);
  readonly error = signal("");

  readonly selectedName = signal<string | null>(null);
  readonly source = signal("");
  readonly loadingSource = signal(false);

  readonly saving = signal(false);
  readonly savedMessage = signal("");
  readonly saveError = signal("");

  readonly dirty = signal(false);

  /** Rendered preview HTML (srcdoc), updated by debounced live preview. */
  readonly previewHtml = signal<SafeHtml | null>(null);
  readonly previewing = signal(false);
  readonly previewError = signal("");

  private readonly previewSubject = new Subject<{ name: string; source: string }>();

  readonly selectedMeta = computed(() =>
    this.templates().find((t) => t.name === this.selectedName()) ?? null,
  );

  constructor() {
    this.previewSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(
          (a, b) => a.name === b.name && a.source === b.source,
        ),
        switchMap(({ name, source }) =>
          this.api.previewEmailSource(name, source).pipe(
            catchError((err) => {
              this.previewError.set(this.extractErr(err));
              return of(null);
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((html) => {
        this.previewing.set(false);
        if (html === null) return; // error path above set previewError
        this.previewError.set("");
        this.previewHtml.set(this.sanitizer.bypassSecurityTrustHtml(html));
      });

    this.loadList();
  }

  private loadList() {
    this.loading.set(true);
    this.api
      .listEmailTemplates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (list) => {
          this.templates.set(list ?? []);
          this.loading.set(false);
          if (list?.length && !this.selectedName()) {
            this.select(list[0].name);
          }
        },
        error: (err) => {
          this.loading.set(false);
          this.error.set(
            "Could not load email templates. " + this.extractErr(err),
          );
        },
      });
  }

  async select(name: string) {
    if (name === this.selectedName() && this.source()) {
      return;
    }
    if (
      this.dirty() &&
      !(await this.confirmDialog.confirm({
        title: "Discard changes",
        message: "You have unsaved changes. Switch templates and discard them?",
        confirmText: "Discard",
        destructive: true,
      }))
    ) {
      return;
    }
    this.selectedName.set(name);
    this.loadingSource.set(true);
    this.previewHtml.set(null);
    this.previewError.set("");
    this.saveError.set("");
    this.savedMessage.set("");
    this.dirty.set(false);
    this.api
      .getEmailTemplate(name)
      .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
      .subscribe((t) => {
        this.loadingSource.set(false);
        if (!t) {
          this.error.set(`Could not load template "${name}".`);
          return;
        }
        this.source.set(t.source);
        this.error.set("");
        this.requestPreview();
      });
  }

  onEdit(value: string) {
    this.source.set(value);
    this.dirty.set(true);
    this.savedMessage.set("");
    this.saveError.set("");
    this.requestPreview();
  }

  private requestPreview() {
    const name = this.selectedName();
    if (!name) return;
    this.previewing.set(true);
    this.previewError.set("");
    this.previewSubject.next({ name, source: this.source() });
  }

  save() {
    const name = this.selectedName();
    if (!name) return;
    this.saving.set(true);
    this.saveError.set("");
    this.savedMessage.set("");
    this.api
      .saveEmailTemplate(name, this.source())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (saved) => {
          this.saving.set(false);
          this.dirty.set(false);
          this.savedMessage.set(
            "Saved at " + this.fmt(saved.updatedAt ?? new Date().toISOString()),
          );
          const meta = this.templates().map((t) =>
            t.name === name
              ? { ...t, overridden: true, updatedAt: saved.updatedAt }
              : t,
          );
          this.templates.set(meta);
          this.error.set("");
          this.requestPreview();
        },
        error: (err) => {
          this.saving.set(false);
          this.saveError.set(this.extractErr(err));
        },
      });
  }

  async reset() {
    const name = this.selectedName();
    if (!name) return;
    const confirmed = await this.confirmDialog.confirm({
      title: "Reset template",
      message: `Reset "${name}" to its built-in default? Your custom version will be discarded.`,
      confirmText: "Reset",
      destructive: true,
    });
    if (!confirmed) {
      return;
    }
    this.saving.set(true);
    this.api
      .resetEmailTemplate(name)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (t) => {
          this.saving.set(false);
          this.dirty.set(false);
          this.source.set(t.source);
          this.savedMessage.set("Reset to built-in default.");
          this.saveError.set("");
          this.templates.set(
            this.templates().map((m) =>
              m.name === name
                ? { ...m, overridden: false, updatedAt: undefined }
                : m,
            ),
          );
          this.requestPreview();
        },
        error: (err) => {
          this.saving.set(false);
          this.saveError.set(this.extractErr(err));
        },
      });
  }

  private extractErr(err: unknown): string {
    const e = err as {
      error?: { message?: string };
      message?: string;
      status?: number;
    };
    if (e?.error?.message) return e.error.message;
    if (e?.message) return e.message;
    return "Something went wrong. Check the server logs.";
  }

  private fmt(iso: string): string {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  }
}
